import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  Pagination,
  Table,
  Text,
} from "@chakra-ui/react";
import { LuCheck, LuPlus, LuSearch } from "react-icons/lu";
import { ProductState } from "./ProductsTable";
import { Checkbox } from "@/ui/chakra/checkbox";

import { useEffect, useRef, useState } from "react";
import { Pagination as PaginationType } from "@/core/common/types";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { useGetProducts } from "@/shared/core-products";
import { useAuth } from "@/shared/session";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";

export interface ModalSearchProductProps {
  isOpen: boolean;
  onClose: () => void;
  selected: ProductState[];
  onChange: (products: ProductState) => void;
  productType?: "all" | "local" | "erp";
  storageId?: string;
}

const headerProps = {
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "wider",
  textTransform: "uppercase",
  color: "fg.muted",
  whiteSpace: "nowrap",
} as const;

export const ModalSearchProduct = ({
  isOpen,
  onClose,
  selected = [],
  onChange,
  productType = "all",
  storageId,
}: ModalSearchProductProps) => {
  const { session } = useAuth();

  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [storageId]);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [withStock, setWithStock] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleToggleProduct = (product: ProductState) => {
    onChange(product);
    searchInputRef.current?.focus();
    searchInputRef.current?.select();
  };
  const { data: products, pagination: productsPagination } = useGetProducts({
    page: pagination.page,
    pageSize: pagination.pageSize,
    searchTerm: debouncedSearchTerm,
    type: productType,
    storageId,
    zoneId: session?.zone ?? undefined,
    withStock,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, withStock]);

  return (
    <DialogRoot
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
      initialFocusEl={() => searchInputRef.current}
    >
      <DialogContent maxW="1200px" w="95vw">
        <DialogHeader borderBottomWidth="1px" borderColor="border">
          <Box>
            <DialogTitle>Productos disponibles</DialogTitle>
            <Text fontSize="sm" color="fg.muted" fontWeight="normal" mt={1}>
              Haz clic en un producto para agregarlo o quitarlo de la selección.
            </Text>
          </Box>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody display="flex" flexDirection="column" gap={4} pt={4}>
          <HStack gap={4} flexWrap="wrap">
            <InputGroup startElement={<LuSearch />} maxW="360px">
              <Input
                ref={searchInputRef}
                placeholder="Codigo, descripcion, etc..."
                aria-label="Buscar producto"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  if (products?.length === 1) {
                    handleToggleProduct({ data: products[0], amount: 1 });
                  }
                }}
              />
            </InputGroup>
            <Checkbox
              colorPalette="brand"
              checked={withStock}
              onCheckedChange={(e) => setWithStock(e.checked === true)}
            >
              Solo con stock
            </Checkbox>
          </HStack>

          <Box borderWidth="1px" borderColor="border" rounded="lg">
            <Table.ScrollArea>
              <Table.Root size="sm" variant="line">
                <Table.Header bg="bg.subtle">
                  <Table.Row>
                    <Table.ColumnHeader width="1%" />
                    <Table.ColumnHeader {...headerProps}>
                      Código microsiga
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps}>
                      Descripción
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps}>
                      Bodega
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps} textAlign="end">
                      Stock
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps}>
                      Unidad
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps} textAlign="end">
                      Conversor
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps}>
                      Tipo conversor
                    </Table.ColumnHeader>
                    <Table.ColumnHeader {...headerProps}>
                      Tipo de producto
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {products?.map((product) => {
                    const isSelected = !!selected.find(
                      (p) => p.data.id === product.id,
                    );
                    const imageUrl = product.externalProduct?.imageUrl;

                    return (
                      <Table.Row
                        key={product.id}
                        bg={isSelected ? "brand.subtle" : undefined}
                        _hover={{
                          bg: isSelected ? "brand.subtle" : "bg.subtle",
                        }}
                        cursor="pointer"
                        onClick={() =>
                          handleToggleProduct({ data: product, amount: 1 })
                        }
                      >
                        <Table.Cell>
                          <IconButton
                            aria-label={
                              isSelected
                                ? "Quitar de la selección"
                                : "Agregar producto"
                            }
                            size="sm"
                            variant={isSelected ? "solid" : "outline"}
                            colorPalette={isSelected ? "green" : "gray"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleProduct({ data: product, amount: 1 });
                            }}
                          >
                            {isSelected ? <LuCheck /> : <LuPlus />}
                          </IconButton>
                        </Table.Cell>
                        <Table.Cell fontWeight="semibold" whiteSpace="nowrap">
                          {product.externalProduct?.code ||
                            product.erpProduct?.erpCode}
                        </Table.Cell>
                        <Table.Cell minW="220px">
                          <Flex align="center" gap={3}>
                            {imageUrl && (
                              <a
                                href={imageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Image
                                  src={imageUrl}
                                  alt={product.description}
                                  boxSize="32px"
                                  objectFit="contain"
                                  rounded="md"
                                />
                              </a>
                            )}
                            <Text>{product.description}</Text>
                          </Flex>
                        </Table.Cell>
                        <Table.Cell whiteSpace="nowrap">
                          {product.storage.name}
                        </Table.Cell>
                        <Table.Cell textAlign="end">{product.stock}</Table.Cell>
                        <Table.Cell>{product.unit}</Table.Cell>
                        <Table.Cell textAlign="end">
                          {product.converter}
                        </Table.Cell>
                        <Table.Cell>{product.converterType.name}</Table.Cell>
                        <Table.Cell>
                          <Badge
                            variant="subtle"
                            colorPalette={
                              product.externalProduct ? "blue" : "purple"
                            }
                          >
                            {product.externalProduct ? "Local" : "ERP"}
                          </Badge>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                  {products?.length === 0 && (
                    <Table.Row>
                      <Table.Cell
                        colSpan={9}
                        textAlign="center"
                        py={8}
                        color="fg.muted"
                      >
                        No se encontraron productos
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          </Box>

          {productsPagination && (
            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
              <Text fontSize="sm" color="fg.muted">
                {selected.length} seleccionado(s)
              </Text>
              <Pagination.Root
                count={Math.max(productsPagination?.totalPages ?? 1, 1)}
                pageSize={1}
                page={pagination.page}
                onPageChange={(e) =>
                  setPagination({ ...pagination, page: e.page })
                }
                siblingCount={1}
              >
                <ButtonGroup variant="outline" size="sm" gap={1}>
                  <Pagination.PrevTrigger asChild>
                    <Button aria-label="Anterior">Anterior</Button>
                  </Pagination.PrevTrigger>
                  <Pagination.Items
                    render={(page) => (
                      <IconButton
                        aria-label={`Página ${page.value}`}
                        variant={{ base: "outline", _selected: "solid" }}
                        colorPalette="brand"
                      >
                        {page.value}
                      </IconButton>
                    )}
                  />
                  <Pagination.NextTrigger asChild>
                    <Button aria-label="Siguiente">Siguiente</Button>
                  </Pagination.NextTrigger>
                </ButtonGroup>
              </Pagination.Root>
            </Flex>
          )}
        </DialogBody>

        <DialogFooter borderTopWidth="1px" borderColor="border">
          <Button colorPalette="brand" onClick={() => onClose()} minW="160px">
            Aceptar
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
